// React hook that exposes a rolling stream of pipeline-stage events for
// the Document Intelligence Hub's "in flight" queue. Built on top of
// `useOcrTrackingMessages` (which handles connection lifecycle, table /
// process / user scoping). This hook is a higher-level convenience that
// distills each frame down to an ExtractionStageEvent, the shape the
// live-queue UI was originally designed against.
//
// Frame → event projection:
//   - For insert/update on `doc_extraction_tracking`, derive a stage
//     event from `id_details.{process_id, status}`.
//   - Other actions (process_complete, etc.) are passed through too —
//     the live queue ignores ones without a `status`.

import { useCallback, useEffect, useRef, useState } from "react";

import { TABLES } from "../socket/constants";
import { useOcrTrackingMessages } from "./useOcrTrackingMessages";
import type {
  DocumentRecord,
  ExtractionStageEvent,
  OcrTrackingFrame,
} from "../types/documents";

interface UseExtractionLiveOptions {
  /** Cap how many recent events to keep in memory. Default 200. */
  bufferSize?: number;
  /** Disable the connection. Useful when the parent route is hidden. */
  paused?: boolean;
  /**
   * Override the backend table to filter on. Defaults to
   * "doc_extraction_tracking" — the inbox's table.
   */
  tableName?: string;
}

interface UseExtractionLiveResult {
  events: ExtractionStageEvent[];
  connected: boolean;
  error: string | null;
  /** Imperatively clear the event buffer (e.g. when the user opens a doc). */
  clear: () => void;
}

function frameToStageEvent(
  frame: OcrTrackingFrame,
  at: string,
): ExtractionStageEvent | null {
  const details = frame.id_details as Partial<DocumentRecord> | undefined;
  const id = frame.process_id || details?.process_id;
  const status = details?.status;
  if (!id || !status) return null;
  return {
    process_id: id,
    status,
    at,
    message: frame.message ?? null,
  };
}

export function useExtractionLive(
  options: UseExtractionLiveOptions = {},
): UseExtractionLiveResult {
  const {
    bufferSize = 200,
    paused = false,
    tableName = TABLES.DOC_EXTRACTION_TRACKING,
  } = options;

  const [events, setEvents] = useState<ExtractionStageEvent[]>([]);

  const clear = useCallback(() => setEvents([]), []);

  // Stable handler — the inner hook uses a ref, so re-creating the
  // function on every render wouldn't cause a re-subscribe. We still
  // wrap with useCallback so React's exhaustive-deps lint is happy.
  const handleFrame = useCallback(
    (frame: OcrTrackingFrame) => {
      const now = new Date().toISOString();
      const evt = frameToStageEvent(frame, now);
      if (!evt) return;
      setEvents((prev) => {
        const merged = [evt, ...prev];
        return merged.length > bufferSize
          ? merged.slice(0, bufferSize)
          : merged;
      });
    },
    [bufferSize],
  );

  // Keep the handler ref fresh without forcing the inner hook to re-sub.
  const handlerRef = useRef(handleFrame);
  useEffect(() => {
    handlerRef.current = handleFrame;
  }, [handleFrame]);

  const { connected, error } = useOcrTrackingMessages(
    (frame) => handlerRef.current(frame),
    { tableName, paused },
  );

  return { events, connected, error, clear };
}
